import React from "react";
import { NavLink } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";

const Footer = () => {
  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="relative bg-[#0B5647] text-white overflow-hidden font-poppins pt-10 pb-6 md:pt-16 md:pb-8">
      {/* Background overlay shapes */}
      <div className="absolute inset-0 z-0 pointer-events-none hidden md:block">
        {/* Large diagonal triangle */}
        <div
          className="absolute inset-0 bg-black/[0.07]"
          style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
        ></div>

        {/* Bottom-right triangle */}
        <div
          className="absolute bottom-0 right-0 w-full h-[60%] bg-white/[0.03]"
          style={{ clipPath: "polygon(100% 100%, 0 100%, 100% 0)" }}
        ></div>

        {/* Top-right triangle */}
        <div
          className="absolute top-0 right-0 w-1/2 h-full bg-white/[0.04]"
          style={{ clipPath: "polygon(100% 0, 20% 0, 100% 80%)" }}
        ></div>
      </div>

      <div className="max-w-[1280px] px-6 mx-auto md:px-12 relative z-10 flex flex-col gap-10 md:gap-14">

        {/* Top Row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">

          <div>
            <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider font-semibold mb-1">
              Get In Touch
            </p>

            <a
              href="mailto:support@airportparking.com"
              className="text-2xl sm:text-3xl md:text-4xl font-bold border-b-2 border-white pb-1 inline-block hover:text-brandLime hover:border-brandLime transition-all duration-300"
            >
              support@airportparking.com
            </a>
          </div>

          <div>
            <span className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-wider leading-none block select-none">
              AIRPARK
            </span>
          </div>
        </div>

        {/* Middle Row */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-8">

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row gap-6 w-full lg:w-[60%]">

            {/* Location */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-brandLime text-lg sm:text-xl">
                  <FaMapMarkerAlt />
                </div>

                <h3 className="text-sm sm:text-base font-bold uppercase tracking-wide">
                  Location
                </h3>
              </div>

              <p className="text-xs sm:text-sm text-gray-300 pr-2 leading-relaxed">
                Reliable and secure airport parking services with affordable
                prices, easy booking, and stress-free travel experience.
              </p>
            </div>

            {/* Phone */}
            <div className="flex-1 sm:border-l sm:border-[#2e5d4f] sm:pl-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-brandLime text-lg sm:text-xl">
                  <FaPhoneAlt />
                </div>

                <h3 className="text-sm sm:text-base font-bold uppercase tracking-wide">
                  Phone Number
                </h3>
              </div>

              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                +44 1234 567 890
              </p>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="w-full lg:w-[40%] flex flex-col items-start lg:items-end mt-4 lg:mt-0">

            {/* Top Links */}
            <ul className="flex flex-wrap justify-start lg:justify-end gap-x-2 sm:gap-x-3 gap-y-1.5 text-xs sm:text-sm font-medium">

              <li>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    isActive
                      ? "text-brandLime transition-colors"
                      : "text-white hover:text-brandLime transition-colors"
                  }
                  onClick={scrollToTop}
                >
                  Home
                </NavLink>
              </li>

              <li className="text-gray-500">|</li>

              <li>
                <NavLink
                  to="/faqs"
                  className={({ isActive }) =>
                    isActive
                      ? "text-brandLime transition-colors"
                      : "text-white hover:text-brandLime transition-colors"
                  }
                  onClick={scrollToTop}
                >
                  FAQs
                </NavLink>
              </li>

              <li className="text-gray-500">|</li>

              <li>
                <NavLink
                  to="/contact-us"
                  className={({ isActive }) =>
                    isActive
                      ? "text-brandLime transition-colors"
                      : "text-white hover:text-brandLime transition-colors"
                  }
                  onClick={scrollToTop}
                >
                  Contact Us
                </NavLink>
              </li>
            </ul>

            {/* Bottom Links */}
            <ul className="flex flex-wrap justify-start lg:justify-end gap-x-2 sm:gap-x-3 gap-y-1.5 text-xs sm:text-sm font-medium mt-2 sm:mt-3">

              <li>
                <NavLink
                  to="/terms-and-conditions"
                  className={({ isActive }) =>
                    isActive
                      ? "text-brandLime transition-colors"
                      : "text-white hover:text-brandLime transition-colors"
                  }
                  onClick={scrollToTop}
                >
                  Terms & Conditions
                </NavLink>
              </li>

              <li className="text-gray-500">|</li>

              <li>
                <NavLink
                  to="/privacy-policy"
                  className={({ isActive }) =>
                    isActive
                      ? "text-brandLime transition-colors"
                      : "text-white hover:text-brandLime transition-colors"
                  }
                  onClick={scrollToTop}
                >
                  Privacy Policy
                </NavLink>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 md:gap-0 mt-2 md:mt-4">

          {/* Brand Name */}
          <div className="w-full md:w-auto text-left">
            <h2 className="text-[55px] sm:text-[75px] md:text-[90px] lg:text-[100px] font-bold text-brandLime leading-[0.8] tracking-tighter select-none">
              AIRPARK.
            </h2>
          </div>

          {/* Copyright */}
          <div className="w-full md:w-auto text-left md:text-right text-[10px] sm:text-xs text-gray-400 flex flex-col justify-end gap-0.5">

            <p>
              Safe, secure, and affordable airport parking solutions for every traveler.
            </p>

            <p>
              © 2025 Airport Parking. All Rights Reserved.
            </p>

            <p>
              Developed by{" "}
              <span className="font-bold text-white">
                Binary Ops Pvt Ltd.
              </span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;